import type { CredentialResponse } from "@react-oauth/google";
import type { User } from "schemas/generated/user";
import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";

import * as React from "react";

import { authRouter } from "@v2/trpc/routes/auth";
import some from "lodash-es/some";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-utils/dom/misc";
import { AGENT_ONBOARDING_START_PATH } from "replo-utils/lib/agentApplicationPaths";
import { isEmpty } from "replo-utils/lib/misc";
import * as z from "zod";

import { usePostAuthRedirect } from "./usePostAuthRedirect";

const emailSchema = z
  .email()
  .refine(
    (email) => email.length <= 254,
    "Use an email address with 254 characters or fewer.",
  );
const passwordSchema = z
  .string()
  .min(8, "Passwords must contain at least 8 characters.")
  .max(100, "Passwords must contain at most 100 characters.");

// `source` also carries free-form campaign codes, which the signup endpoint would reject.
function getRequestedSignupSource(
  searchParams: URLSearchParams,
): "artifact_comment" | undefined {
  return searchParams.get("source") === "artifact_comment"
    ? "artifact_comment"
    : undefined;
}

type SignupFormValues = {
  email: string;
  password: string;
};

export const useSignupForm = ({
  defaultProjectType,
}: { defaultProjectType?: WorkspaceDefaultProjectType } = {}) => {
  const [searchParams] = useSearchParams();

  const defaultProjectTypeInput = defaultProjectType ?? "shopify_theme_builder";

  const redirectAfterAuth = usePostAuthRedirect();
  const handleAccountCreation = ({
    token,
    user,
    isNewAccount,
  }: {
    token: string;
    user: User;
    isNewAccount: boolean;
  }) => {
    // The steps live in the Agent app: loading "/" first costs a second full page load just to be redirected there.
    const isNewAgentSignup =
      isNewAccount &&
      defaultProjectTypeInput === "agent" &&
      // The server keeps a Shopify install on the Shopify funnel whatever was picked here; its workspace cannot own an Agent project.
      user.source !== "shopify";
    void redirectAfterAuth({
      token,
      projectlessPath: isNewAgentSignup ? AGENT_ONBOARDING_START_PATH : "/",
      isNewAccount,
    });
  };

  // The funnel travels as the workspace's defaultProjectType on one shared route
  // pair, so the surface a user came from picks the funnel — no separate routes.
  const googleAccountMutation = authRouter.createAccountWithGoogle.useMutation({
    meta: { reploIgnoreDefaultErrorHandling: true },
    onSuccess: handleAccountCreation,
  });
  const { mutate: createGoogleAccountApi } = googleAccountMutation;

  const accountMutation = authRouter.createAccount.useMutation({
    meta: { reploIgnoreDefaultErrorHandling: true },
    onSuccess: handleAccountCreation,
  });
  const { mutate: createAccountApi } = accountMutation;

  const isLoadingCreateAccount = accountMutation.isPending;

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    control,
  } = useForm<SignupFormValues>({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : (searchParams.get("email") ?? ""),
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });
  const someFieldIsEmpty = some(Object.values(watchAllFields), isEmpty);

  const onGoogleSubmit = React.useCallback(
    async (credentialResponse: CredentialResponse) => {
      const { clientId, credential } = credentialResponse;
      if (clientId && credential) {
        const pendingTemplateId = sessionStorage.getItem(
          "replo_pending_template_id",
        );
        createGoogleAccountApi({
          clientId,
          credential,
          installationNonce: searchParams.get("nonce"),
          defaultProjectType: defaultProjectTypeInput,
          source: getRequestedSignupSource(searchParams),
          ...(pendingTemplateId ? { pendingTemplateId } : {}),
        });
      }
    },
    [createGoogleAccountApi, searchParams, defaultProjectTypeInput],
  );

  const onSubmit = React.useCallback(
    async ({ email, password }: SignupFormValues) => {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      createAccountApi({
        email,
        password,
        installationNonce: searchParams.get("nonce"),
        defaultProjectType: defaultProjectTypeInput,
        source: getRequestedSignupSource(searchParams),
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    },
    [createAccountApi, searchParams, defaultProjectTypeInput],
  );

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;

  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const buttonShouldBeDisabled =
    someFieldIsEmpty ||
    Boolean(passwordError) ||
    Boolean(emailError) ||
    isLoadingCreateAccount;

  const displayErrorMessage =
    googleAccountMutation.error?.message ??
    accountMutation.error?.message ??
    null;

  const emailRegisterProps = register("email", {
    required: "Please enter a valid email address.",
    validate: (value) => {
      const parsedEmail = emailSchema.safeParse(value);
      if (parsedEmail.success) {
        return true;
      }
      return parsedEmail.error?.issues[0]?.message;
    },
    onChange: () => clearErrors("email"),
  });

  const passwordRegisterProps = register("password", {
    required: "Please enter a valid password.",
    validate: (value) => {
      const parsedPassword = passwordSchema.safeParse(value);
      if (parsedPassword.success) {
        return true;
      }
      return parsedPassword.error?.issues[0]?.message;
    },
    onChange: () => clearErrors("password"),
  });

  return {
    register,
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    errors,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
    control,
  };
};
